import React, { ChangeEvent, useImperativeHandle, useRef, useState } from 'react'
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import { loading } from '../../actions/Loading';
import api from '../../api/api';
import { useDispatch, useSelector } from 'react-redux';
import { Template } from '../../model/Template';
import AlertEx from '../AlertEx';
import { IGlobalState } from '../../model/IGlobalState';
import FormSelectItem from '../form/FormSelectItem';
import { FaCopy, FaCheck } from 'react-icons/fa';
import FormSwitchItem from '../form/FormSwitchItem';
import { isDirtyJson } from '../../util/util';

interface IProps {
    stockId: string;
}

const Style = styled.div`
margin: 2rem 0;
.editor-container {
  display: flex;  
  align-items: center;  
}

.editor-container > div {
    flex: 40%;
    margin-left: 1rem;
}
.templates-select{
    min-width:290px;
    max-width:290px;
}
`;



const ButtonContainer = styled.div`
  margin: 1rem 0;
  display: flex;
  gap: 1rem;
`;

const HtmlTextarea = styled.textarea`
  width: 100%;
  height: 800px;
  font-family: monospace;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin: 1rem 0;
`;

export interface ITemplateMethods {
    save: () => Promise<boolean>
    isDirty: () => boolean
}

const TemplatesComponent = React.forwardRef<any, IProps>((props: IProps, ref) => {

    const dispatch = useDispatch();
    const stock = useSelector((state: IGlobalState) => state.currentStock)
    const [selectedId, setSelectedId] = useState<string>();
    const [htmlSource, setHtmlSource] = useState<string>('');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [isForCustomCampaign, setIsForCustomCampaign] = useState<boolean>(false);
    const [copied, setCopied] = useState(false);
    // Baseline for the currently-selected template, refreshed whenever a template is
    // selected (load, dropdown change, or after save) so switching templates is not "dirty".
    const baseline = useRef<{ html: string; isForCustomCampaign: boolean } | null>(null);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(htmlSource);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    
    useAsyncEffect(async (isMounted: () => boolean) => {
        
        dispatch(loading(true));
        const result = await api().getTemplates(props.stockId);
        if (isMounted()) {
            
            setContent(result.templates);
        }
        dispatch(loading(false));
        
    }, []);

    const setContent = (templates:Template[], name?:string)=>{
                
       
        setTemplates(templates);
        let template = templates.find(x=>x.name===name) || templates[0];

        setSelectedTemplate(template.id!, templates)
    }

    // const handleEditorChange = (content: string) => {        
    //     setHtmlSource(content);
    // };

    const addNewTemplate = ()=> {
        const name = prompt('Enter a name:', 'template_' + new Date().toTimeString().slice(0, 8).replace(/:/g, ''));
        if(name){
            const template = { id: "new", html: '<p>Paste your HTML here</p>', stockId: props.stockId, name, isForCustomCampaign: false };
            const newTemplates = [...templates, template];
            setTemplates(newTemplates);
            setSelectedTemplate(template.id, newTemplates)
        }        
    }
    const renameTemplate = ()=> {
        const template = templates.find(x=>x.id===selectedId)!;
        const name = prompt('Enter new name:', template.name);
        if(name){
            const renamed = { ...template, name: name}
            setTemplates([ ...templates.filter(x=>x.id!==template.id), renamed ]);
        }        
    }
    
    const setSelectedTemplate = (id:string, templates:Template[])=>{
        
        const template = templates.find(x=>x.id===id);
        if(!template){
            console.error(`no template with id ${id}`);
        }
        setSelectedId(id)
        setHtmlSource(template!.html);
        setIsForCustomCampaign(template!.isForCustomCampaign);
        baseline.current = { html: template!.html, isForCustomCampaign: template!.isForCustomCampaign };
    }


    useImperativeHandle(ref, (): ITemplateMethods => ({

        isDirty() {
            return baseline.current != null && isDirtyJson({ html: htmlSource, isForCustomCampaign }, baseline.current);
        },

        async save() {
           
            let template = templates.find(x=>x.id===selectedId)!;
            template = {
                ...template,
                id: template.id==="new" ? null: template.id,                
                html : htmlSource,
                isForCustomCampaign: isForCustomCampaign
            }            
            const result = await api().saveTemplate(template);
            if (result.success) {
                setContent(result.templates, template.name);
                AlertEx('Saved!');
            } else {
                AlertEx(`Error! ${result.errorMessage}`);
            }

            return true;

        }

    }));

    let previewContent = "";
    if(selectedId){
        previewContent = htmlSource.replaceAll("{{ticker}}", stock.ticker)
        if(stock.announcements.length){
            previewContent = previewContent.replaceAll("{{announcement_headline}}", stock.announcements[0].headline)
            previewContent = previewContent.replaceAll("{{announcement_url}}", `https://api.investi.com.au${stock.announcements[0].localPath!}`)
        }

        const summary = `<ul>
<li>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</li>
<li>Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat duis aute irure.</li>
<li>Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</li>
</ul>`;
        previewContent = previewContent.replaceAll("{{summary}}", summary)
    }


    const items = templates.map(x=> ({text:x.name, value: x.id!}));
    return <Style>
      

        {selectedId && (
            <>
            <div className='horizontal'>
                <FormSelectItem className="templates-select" label="Templates"  value={selectedId} 
                onChange={(e:ChangeEvent<HTMLSelectElement>)=>setSelectedTemplate(e.target.value, templates)} 
                items={items} />
                    <button onClick={addNewTemplate}>Add New Template</button>
                    <button onClick={renameTemplate}>Rename</button>
                    <FormSwitchItem label="Available for custom Campaign"  value={isForCustomCampaign} onChange={(name:string, value:string|boolean) => setIsForCustomCampaign(value as boolean)}
                        />
            </div>
            
            
                <ButtonContainer>
                </ButtonContainer>
                <div style={{ marginBottom: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '4px', fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                    <strong>Tip:</strong> To edit a template with AI assistance:
                    <strong>1.</strong> Copy the HTML below using the button, then paste into <a href="https://chatgpt.com" target="_blank" rel="noopener noreferrer">ChatGPT</a>.
                    <strong>2.</strong> Describe your changes and remind it to keep the HTML email-client friendly and spam/junk filter safe.
                    <strong>3.</strong> Copy the result and paste it back here, then preview and save. (top right)
                </div>
                <div className='editor-container'>

                <div style={{ flex: '60%' }}>
                    <button onClick={copyToClipboard} style={{ marginBottom: '0.5rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                        {copied ? <><FaCheck style={{ color: 'green' }} /> Copied!</> : <><FaCopy /> Copy HTML</>}
                    </button>
                <HtmlTextarea style={{ width: '100%' }}
                        value={htmlSource}
                        onChange={e=>setHtmlSource(e.target.value)}
                        placeholder="Paste your HTML here..."
                    />
                </div>

                {/* Preview */}
                <div >
                    <h4>Preview</h4>
                    <iframe
                        srcDoc={previewContent}
                        style={{ 
                            width: '100%',
                            height: '800px',
                            border: '1px solid #ccc'
                        }}
                        title="HTML Preview"
                        sandbox="allow-same-origin"
                    />
                </div>

                </div>
            </>
        )}

       
    </Style>
});

export default TemplatesComponent;